import type { ArgsOf } from "../../../access/types.ts";
import { Access } from "../../../access/types.ts";

const instanceAccessParameters = {
  environment: {
    type: "dynamic",
    label: "Environment",
    optional: true as const,
  },
  environmentId: {
    type: "generated",
    label: "Environment ID",
  },
  hostId: {
    type: "dynamic",
    label: "Host ID",
  },
  hostname: {
    type: "generated",
    label: "Hostname",
  },
  clientId: {
    type: "generated",
    label: "Client ID",
  },
  publicIp: {
    type: "generated",
    label: "Public IP",
  },
  fingerprint: {
    type: "generated",
    label: "Fingerprint",
  },
} as const;

const linuxAccessParameters = {
  linuxUserName: {
    type: "generated",
    label: "Linux user name",
  },
} as const;

export const SelfHostedAccesses = {
  computer: Access({
    description: "Provision user access to an individual machine",
    label: "Provision access to computer",
    maturity: "ga",
    delegation: {},
    generated: {
      groupName: { type: "string", optional: true as const },
    },
    parameters: {
      ...instanceAccessParameters,
      ...linuxAccessParameters,
      sudo: {
        type: "boolean",
        label: "Sudo",
        valueLabel: "Grant sudo access",
        optional: true,
      },
      publicKey: {
        type: "input",
        label: "Public key",
        optional: true,
      },
    },
    render: { locator: "hostId", policy: "sudo" } as const,
  }),
  user: Access({
    description: "Provision access to another user on a machine",
    label: "Provision access to a user on a computer",
    maturity: "preview",
    delegation: {},
    generated: {
      groupName: {
        type: "string",
      },
    },
    parameters: {
      ...instanceAccessParameters,
      ...linuxAccessParameters,
      user: { type: "dynamic", label: "User" },
    },
    render: { locator: "hostId", policy: "user" } as const,
  }),
};

export type SelfHostedAccesses = typeof SelfHostedAccesses;
export type SelfHostedAccess = SelfHostedAccesses[keyof SelfHostedAccesses];

export type SelfHostedComputerAccess = SelfHostedAccesses["computer"];
export type SelfHostedComputerAccessArgs = ArgsOf<SelfHostedComputerAccess>;

export type SelfHostedUserAccess = SelfHostedAccesses["user"];
export type SelfHostedUserAccessArgs = ArgsOf<SelfHostedUserAccess>;
